import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  Tabs,
  Tab,
  Button,
  Chip,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Tooltip,
  Badge,
  Skeleton,
  alpha,
  useTheme,
  useMediaQuery,
  Fab,
  SwipeableDrawer,
  Stack,
  Divider,
} from '@mui/material';
import {
  Send,
  Inbox,
  Search,
  Plus,
  Filter,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  MoreVertical,
  MessageSquare,
  Calendar,
  ArrowUpRight,
  RefreshCw,
  X,
  ChevronRight,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { useAppSelector, useAppDispatch } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { openModal } from '@/store/slices/uiSlice';
import { DelegatedTask, DelegatedTaskStatus, TaskPriority } from '@/types';
import { delegatedTaskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import PriorityBadge from '@/shared/components/PriorityBadge';
import CreateDelegatedTaskModal from '../components/CreateDelegatedTaskModal';
import DelegatedTaskDetailModal from '../components/DelegatedTaskDetailModal';

// Status configuration
const statusConfig: Record<DelegatedTaskStatus, { label: string; color: string; icon: React.ElementType }> = {
  [DelegatedTaskStatus.PENDING]: { label: 'Pending', color: '#f59e0b', icon: Clock },
  [DelegatedTaskStatus.ACCEPTED]: { label: 'Accepted', color: '#3b82f6', icon: CheckCircle },
  [DelegatedTaskStatus.IN_PROGRESS]: { label: 'In Progress', color: '#8b5cf6', icon: RefreshCw },
  [DelegatedTaskStatus.COMPLETED]: { label: 'Completed', color: '#16a34a', icon: CheckCircle },
  [DelegatedTaskStatus.REJECTED]: { label: 'Rejected', color: '#ef4444', icon: XCircle },
  [DelegatedTaskStatus.CANCELLED]: { label: 'Cancelled', color: '#6b7280', icon: XCircle },
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      {...other}
      sx={{ py: 2 }}
    >
      {value === index && children}
    </Box>
  );
}

/**
 * DelegatedTasksPage
 * Main page for managing delegated tasks (one-on-one assignments)
 * Two views: Tasks I've delegated (Sent) and Tasks assigned to me (Received)
 * Mobile-first responsive design
 */
const DelegatedTasksPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentUser = useAppSelector(selectCurrentUser);
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const [tabValue, setTabValue] = useState(0); // 0 = Delegated by me, 1 = Assigned to me
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<DelegatedTaskStatus | 'all'>('all');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<DelegatedTask | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalTab, setDetailModalTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTask, setMenuTask] = useState<DelegatedTask | null>(null);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // Fetch delegated tasks (tasks I've assigned to others)
  const { data: delegatedData, isLoading: delegatedLoading } = useQuery({
    queryKey: [...QUERY_KEYS.delegatedTasks, 'delegated', currentUser?.id],
    queryFn: () => delegatedTaskService.getDelegatedTasks(currentUser?.id || ''),
    enabled: !!currentUser?.id,
  });

  // Fetch assigned tasks (tasks assigned to me)
  const { data: assignedData, isLoading: assignedLoading } = useQuery({
    queryKey: [...QUERY_KEYS.delegatedTasks, 'assigned', currentUser?.id],
    queryFn: () => delegatedTaskService.getAssignedTasks(currentUser?.id || ''),
    enabled: !!currentUser?.id,
  });

  const delegatedTasks = delegatedData?.items ?? [];
  const assignedTasks = assignedData?.items ?? [];

  const currentTasks = tabValue === 0 ? delegatedTasks : assignedTasks;
  const isLoading = tabValue === 0 ? delegatedLoading : assignedLoading;

  // Filter tasks
  const filteredTasks = currentTasks.filter((task) => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Count by status for badges
  const getStatusCounts = (tasks: DelegatedTask[]) => {
    return {
      pending: tasks.filter(t => t.status === DelegatedTaskStatus.PENDING).length,
      inProgress: tasks.filter(t => t.status === DelegatedTaskStatus.IN_PROGRESS || t.status === DelegatedTaskStatus.ACCEPTED).length,
      completed: tasks.filter(t => t.status === DelegatedTaskStatus.COMPLETED).length,
    };
  };

  const delegatedCounts = getStatusCounts(delegatedTasks);
  const assignedCounts = getStatusCounts(assignedTasks);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSearchQuery('');
    setStatusFilter('all');
  };

  const handleTaskClick = (task: DelegatedTask) => {
    setSelectedTask(task);
    setDetailModalOpen(true);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, task: DelegatedTask) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuTask(task);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuTask(null);
  };

  const isOverdue = (task: DelegatedTask) => {
    return dayjs(task.dueDate).isBefore(dayjs()) && 
           task.status !== DelegatedTaskStatus.COMPLETED && 
           task.status !== DelegatedTaskStatus.CANCELLED;
  };

  const renderTaskCard = (task: DelegatedTask) => {
    const status = statusConfig[task.status];
    const StatusIcon = status.icon;
    const overdue = isOverdue(task);

    return (
      <Card
        key={task.id}
        onClick={() => handleTaskClick(task)}
        sx={{
          p: 2,
          mb: 1.5,
          cursor: 'pointer',
          border: '1px solid',
          borderColor: overdue ? alpha('#ef4444', 0.3) : 'divider',
          bgcolor: overdue ? alpha('#ef4444', 0.02) : 'background.paper',
          borderRadius: 2,
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transform: 'translateY(-1px)',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {task.title}
              </Typography>
              {task.isUrgent && (
                <Chip
                  label="Urgent"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    bgcolor: alpha('#ef4444', 0.1),
                    color: '#ef4444',
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>
            {task.description && (
              <Typography variant="body2" color="text.secondary" sx={{ 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}>
                {task.description}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, task)}
            sx={{ mt: -0.5 }}
          >
            <MoreVertical size={16} />
          </IconButton>
        </Box>

        {/* Status & Priority Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
          <Chip
            icon={<StatusIcon size={12} />}
            label={status.label}
            size="small"
            sx={{
              height: 24,
              bgcolor: alpha(status.color, 0.1),
              color: status.color,
              fontWeight: 500,
              '& .MuiChip-icon': { color: status.color },
            }}
          />
          <PriorityBadge priority={task.priority} size="small" />
          {overdue && (
            <Chip
              icon={<AlertCircle size={12} />}
              label="Overdue"
              size="small"
              sx={{
                height: 24,
                bgcolor: alpha('#ef4444', 0.1),
                color: '#ef4444',
                fontWeight: 500,
                '& .MuiChip-icon': { color: '#ef4444' },
              }}
            />
          )}
        </Box>

        {/* Assignee/Delegator & Due Date */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
              {tabValue === 0 
                ? task.assignedToName?.charAt(0) || 'U'
                : task.assignedByName?.charAt(0) || 'U'
              }
            </Avatar>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {tabValue === 0 ? 'Assigned to' : 'Delegated by'}
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {tabValue === 0 ? task.assignedToName : task.assignedByName}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: overdue ? 'error.main' : 'text.secondary' }}>
            <Calendar size={14} />
            <Typography variant="caption" fontWeight={overdue ? 600 : 400}>
              {dayjs(task.dueDate).format('MMM D, YYYY')}
            </Typography>
          </Box>
        </Box>

        {/* Feedback indicator */}
        {task.feedback && task.feedback.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <MessageSquare size={14} style={{ color: '#6b7280' }} />
            <Typography variant="caption" color="text.secondary">
              {task.feedback.length} feedback{task.feedback.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        )}
      </Card>
    );
  };

  const renderLoadingSkeleton = () => (
    <Box>
      {[1, 2, 3].map((i) => (
        <Card key={i} sx={{ p: 2, mb: 1.5 }}>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="80%" height={20} />
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Skeleton variant="rounded" width={80} height={24} />
            <Skeleton variant="rounded" width={60} height={24} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width={100} />
          </Box>
        </Card>
      ))}
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {!isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => setCreateModalOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            Delegate Task
          </Button>
        </Box>
      )}

      {/* Tabs */}
      <Card sx={{ mb: { xs: 2, md: 3 }, borderRadius: { xs: 2, md: 1 } }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{
            px: { xs: 1, md: 2 },
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
          }}
        >
          <Tab
            icon={
              <Badge badgeContent={delegatedCounts.pending} color="warning" max={99}>
                <Send size={isMobile ? 16 : 18} />
              </Badge>
            }
            iconPosition="start"
            label={isMobile ? 'Sent' : 'Tasks I Delegated'}
            sx={{ textTransform: 'none', fontWeight: 600, minHeight: { xs: 48, md: 56 }, fontSize: { xs: '0.8rem', md: '0.875rem' } }}
          />
          <Tab
            icon={
              <Badge badgeContent={assignedCounts.pending} color="warning" max={99}>
                <Inbox size={isMobile ? 16 : 18} />
              </Badge>
            }
            iconPosition="start"
            label={isMobile ? 'Received' : 'Tasks Assigned to Me'}
            sx={{ textTransform: 'none', fontWeight: 600, minHeight: { xs: 48, md: 56 }, fontSize: { xs: '0.8rem', md: '0.875rem' } }}
          />
        </Tabs>
      </Card>

      {/* Mobile Toolbar */}
      {isMobile ? (
        <Box sx={{ mb: 2 }}>
          {/* Search Bar */}
          {mobileSearchOpen ? (
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} />
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton onClick={() => { setMobileSearchOpen(false); setSearchQuery(''); }}>
                <X size={20} />
              </IconButton>
            </Box>
          ) : null}
          
          {/* Quick Actions */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <IconButton
              onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
              sx={{
                border: '1px solid',
                borderColor: mobileSearchOpen ? 'primary.main' : 'divider',
                borderRadius: 2,
                color: mobileSearchOpen ? 'primary.main' : 'text.secondary',
              }}
            >
              <Search size={18} />
            </IconButton>
            <IconButton
              onClick={() => setMobileFilterOpen(true)}
              sx={{
                border: '1px solid',
                borderColor: statusFilter !== 'all' ? 'primary.main' : 'divider',
                borderRadius: 2,
                color: statusFilter !== 'all' ? 'primary.main' : 'text.secondary',
              }}
            >
              <Filter size={18} />
            </IconButton>
          </Box>
        </Box>
      ) : (
        /* Desktop Filters */
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            {(['all', DelegatedTaskStatus.PENDING, DelegatedTaskStatus.IN_PROGRESS, DelegatedTaskStatus.COMPLETED] as const).map((status) => (
              <Chip
                key={status}
                label={status === 'all' ? 'All' : statusConfig[status].label}
                onClick={() => setStatusFilter(status)}
                variant={statusFilter === status ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 500,
                  bgcolor: statusFilter === status ? 'primary.main' : 'transparent',
                  color: statusFilter === status ? 'white' : 'text.primary',
              }}
            />
          ))}
        </Box>
      </Box>
      )}

      {/* Task List */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2, pb: isMobile ? 10 : 0 }}>
        {isLoading ? (
          <>
            {renderLoadingSkeleton()}
            {renderLoadingSkeleton()}
            {renderLoadingSkeleton()}
          </>
        ) : filteredTasks.length === 0 ? (
          <Card sx={{ p: { xs: 3, md: 4 }, textAlign: 'center', gridColumn: '1 / -1' }}>
            <Box sx={{ mb: 2 }}>
              {tabValue === 0 ? <Send size={isMobile ? 40 : 48} style={{ color: '#94a3b8' }} /> : <Inbox size={isMobile ? 40 : 48} style={{ color: '#94a3b8' }} />}
            </Box>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="text.secondary" gutterBottom>
              No tasks found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tabValue === 0 
                ? isMobile ? "Tap + to delegate a task" : "You haven't delegated any tasks yet. Click 'Delegate Task' to get started."
                : "No tasks have been assigned to you yet."
              }
            </Typography>
          </Card>
        ) : (
          filteredTasks.map((task) => renderTaskCard(task))
        )}
      </Box>

      {/* Mobile Filter Drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        onOpen={() => setMobileFilterOpen(true)}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '70vh',
            pb: 'env(safe-area-inset-bottom)',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>Filter Tasks</Typography>
            <IconButton onClick={() => setMobileFilterOpen(false)}>
              <X size={20} />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            Status
          </Typography>
          <Stack spacing={1}>
            {(['all', DelegatedTaskStatus.PENDING, DelegatedTaskStatus.IN_PROGRESS, DelegatedTaskStatus.COMPLETED] as const).map((status) => (
              <Box
                key={status}
                onClick={() => { setStatusFilter(status); setMobileFilterOpen(false); }}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  bgcolor: statusFilter === status ? 'primary.light' : 'grey.50',
                  cursor: 'pointer',
                  '&:active': { bgcolor: 'primary.light' },
                }}
              >
                <Typography fontWeight={statusFilter === status ? 600 : 400}>
                  {status === 'all' ? 'All Tasks' : statusConfig[status].label}
                </Typography>
                {statusFilter === status && <ChevronRight size={18} />}
              </Box>
            ))}
          </Stack>
        </Box>
      </SwipeableDrawer>

      {/* Mobile FAB */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="delegate task"
          onClick={() => setCreateModalOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            zIndex: 1000,
          }}
        >
          <Plus size={24} />
        </Fab>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { handleTaskClick(menuTask!); handleMenuClose(); }}>
          <ArrowUpRight size={16} style={{ marginRight: 8 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => {
          if (menuTask) {
            setSelectedTask(menuTask);
            setDetailModalTab(1);
            setDetailModalOpen(true);
          }
          handleMenuClose();
        }}>
          <MessageSquare size={16} style={{ marginRight: 8 }} />
          Add Feedback
        </MenuItem>
      </Menu>

      {/* Create Modal */}
      <CreateDelegatedTaskModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.delegatedTasks });
        }}
      />

      {/* Detail Modal */}
      {selectedTask && (
        <DelegatedTaskDetailModal
          open={detailModalOpen}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedTask(null);
            setDetailModalTab(0);
          }}
          task={selectedTask}
          isAssignee={tabValue === 1}
          initialTab={detailModalTab}
        />
      )}
    </Box>
  );
};

export default DelegatedTasksPage;
